import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import PostLoginLoader from '../../../shared/components/PostLoginLoader';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import AuthService from '../../../shared/services/authService';
import { setUser, setAuthenticated } from '../../../core/store/slices/userSlice';
import type { User } from '../../../shared/types';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToSignup: () => void;
  /** Après connexion réussie ; reçoit l’utilisateur (ex. redirection CM vs joueur). */
  onSuccess: (user: User) => void;
  /** Mode maintenance : pas de lien vers l’inscription. */
  maintenanceCmOnly?: boolean;
}

const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onSwitchToSignup,
  onSuccess,
  maintenanceCmOnly,
}) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPostLoginLoader, setShowPostLoginLoader] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState<{ id: number } | null>(null);
  const { t } = usePageTranslation('public', 'LoginModal');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Fermer avec la touche Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Empêcher le scroll du body quand la modale est ouverte
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const authService = AuthService.getInstance();
      const response = await authService.login({
        email: formData.email,
        password: formData.password
      });
      
      if (response.success && response.user) {
        // Adapter l'utilisateur au format attendu par Redux
        const userForRedux = {
          ...response.user,
          activated: (response.user as any).activated !== undefined ? (response.user as any).activated : true,
          bann: 0,
          premium: false,
          roles_id: (response.user as any).roles_id || 6, // Utiliser le roles_id de l'API ou 6 (Joueur) par défaut
        };
        
        // Stocker le token dans localStorage si présent
        if ((response as any).token) {
          localStorage.setItem('token', (response as any).token);
        } else {
          // Fallback: utiliser l'ID utilisateur comme token temporaire
          localStorage.setItem('token', response.user.id.toString());
        }
        
        // Stocker l'utilisateur dans Redux
        dispatch(setUser(userForRedux as any));
        dispatch(setAuthenticated(true));
        
        // Connexion réussie
        onSuccess(userForRedux as User);
        onClose();
        
        // Réinitialiser le formulaire
        setFormData({
          email: '',
          password: '',
        });
        
        // Afficher l'écran de chargement post-connexion
        setLoggedInUser({ id: response.user.id });
        setShowPostLoginLoader(true);
      } else {
        setError(response.error || t('error.generic'));
      }
    } catch (err) {
      setError(t('error.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handlePostLoginComplete = () => {
    setShowPostLoginLoader(false);
    navigate('/dashboard');
  };

  // Afficher l'écran de chargement post-connexion
  if (showPostLoginLoader && loggedInUser) {
    return (
      <PostLoginLoader 
        userId={loggedInUser.id} 
        onComplete={handlePostLoginComplete}
      />
    );
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-gray-800/95 backdrop-blur-md rounded-xl shadow-2xl w-full max-w-md border-2 border-gray-700/50">
        {/* Header avec logo et titre */}
        <div className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-500 text-white p-6 rounded-t-xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src={`${process.env.PUBLIC_URL}/images/logo.png`} 
                alt="OpSecours" 
                className="w-8 h-8 mr-3"
                onError={(e) => {
                  // Si le logo n'existe pas, masquer l'image
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <h2 className="text-2xl font-bold">{t('title')}</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-white bg-red-600 hover:bg-red-500 rounded-lg p-2 transition-all duration-200 hover:scale-110 border-2 border-white shadow-lg flex items-center justify-center"
              aria-label="Fermer"
            >
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className="text-white"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="bg-red-900/50 border-2 border-red-500/50 text-red-200 px-4 py-3 rounded mb-4 notification-game">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              {t('email')}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              autoComplete="email"
              className="input-game w-full"
              placeholder={t('emailPlaceholder')}
            />
          </div>

          <div className="mb-6">
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
              {t('password')}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                autoComplete="current-password"
                className="input-game w-full pr-12"
                placeholder={t('passwordPlaceholder')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300 transition-colors"
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-game w-full flex items-center justify-center"
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">{t('submit')}...</span>
              </>
            ) : (
              <>
                <i className="bx bx-log-in mr-2"></i>
                {t('submit')}
              </>
            )}
          </button>

          <div className="mt-4 text-center space-y-2">
            {!maintenanceCmOnly && (
            <p className="text-gray-400">
              {t('switch')}{' '}
              <button
                type="button"
                onClick={onSwitchToSignup}
                className="text-primary-400 hover:text-primary-300 font-semibold transition-colors"
              >
                {t('switchLink')}
              </button>
            </p>
            )}
            {maintenanceCmOnly && (
              <p className="text-amber-200/90 text-sm">
                Connexion réservée au compte <strong>Founder</strong> pendant la maintenance.
              </p>
            )}
            <p className="text-gray-400">
              <button
                type="button"
                className="text-secondary-400 hover:text-secondary-300 font-semibold transition-colors underline"
              >
                {t('forgotPassword')}
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginModal;
